"use client";

import { Logo } from "@sum-video/shared/components/Logo";
import { Button } from "@sum-video/shared/components/ui/button";
import { User } from "lucide-react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import type { JSX } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "@/i18n/navigation";
import { loadSupabaseClient } from "@/lib/supabase/loadClient";
import { LocaleSwitcher } from "../LocaleSwitcher";
import { ThemeSwitcher } from "../ThemeSwitcher";
import { DesktopNav } from "./DesktopNav";
import { MobileMenu } from "./MobileMenu";

// Only signed-in visitors see the account menu, so its dropdown code loads
// on demand instead of with every page.
const UserMenu = dynamic(() =>
  import("./UserMenu").then((module) => module.UserMenu),
);

export function Header(): JSX.Element {
  const { user, loading, isSubscribed } = useAuth();
  const t = useTranslations("Header");

  const handleLogout = async (): Promise<void> => {
    const supabase = await loadSupabaseClient();
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Logo LinkComponent={Link} />
          <DesktopNav t={t} user={user} isSubscribed={isSubscribed} />
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center space-x-3">
              <LocaleSwitcher />
              <ThemeSwitcher />
            </div>
            {loading ? (
              <div className="h-9 w-20 bg-muted animate-pulse rounded" />
            ) : user ? (
              <UserMenu t={t} user={user} onLogout={handleLogout} />
            ) : (
              <Button size="sm" asChild>
                <Link href="/auth">
                  <User className="mr-2 h-4 w-4" />
                  {t("getStarted")}
                </Link>
              </Button>
            )}
          </div>
          <MobileMenu
            t={t}
            user={user}
            loading={loading}
            isSubscribed={isSubscribed}
            onLogout={handleLogout}
          />
        </div>
      </div>
    </header>
  );
}
